import { SimpleSettingsProperty, InterpolatedProperty, SettingsProperty } from '../properties';
import { Vector3, Color, TextRenderMode } from '../types';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { AppSettingsData, LabelParentMode } from '../core/AppSettingsData';
import { marked } from 'marked';
import { ScreenPosition } from '../shapes/ScreenHTMLStaticLabelMesh';
import { ArtrackWaypointMeta } from '../integrations/artrack/ArtrackMeta';

// NOTE: This file has been simplified to remove problematic imports after a refactor.
// The constructor now accepts a generic `any` object, and the `POIDataConstructorData`
// type has been commented out to prevent cyclic dependency and import errors.

export class POIData {
    public readonly shape: SimpleSettingsProperty<string>;
    public readonly color: InterpolatedProperty<Color>;
    public readonly opacity: InterpolatedProperty<number>;
    public readonly scale: InterpolatedProperty<number>;
    public readonly localTranslationOffset: SimpleSettingsProperty<Vector3>;

    // 🎯 CLEAN: Primary/Secondary Material System 
    // Only roughness/metalness get primary/secondary - color stays unified!
    public readonly primaryRoughness: InterpolatedProperty<number>;
    public readonly primaryMetalness: InterpolatedProperty<number>;
    public readonly secondaryRoughness: InterpolatedProperty<number>;
    public readonly secondaryMetalness: InterpolatedProperty<number>;

    public readonly position: SimpleSettingsProperty<Vector3>;
    public readonly rotation: SimpleSettingsProperty<Vector3>;
    public readonly markdown: SimpleSettingsProperty<string>;
    public readonly html: SimpleSettingsProperty<string>;
    public readonly uri: SimpleSettingsProperty<string[]>;
    // 🌍 Track/Geo metadata (optional) to support track layouter (prefixed to mark source)
    public readonly artrack_latitude: SimpleSettingsProperty<number>;
    public readonly artrack_longitude: SimpleSettingsProperty<number>;
    public readonly artrack_altitude: SimpleSettingsProperty<number>;
    public readonly artrack_waypointId: SimpleSettingsProperty<number>;
    // Runtime-only metadata from ARTrack (not parsed from markdown)
    public artrackMeta?: ArtrackWaypointMeta;
    public readonly textureBacksideFlipX: SimpleSettingsProperty<boolean>;
    public readonly useBacksideSideMaterial: SimpleSettingsProperty<boolean>;
    public readonly videoAutoplay: SimpleSettingsProperty<boolean>;
    public readonly videoLoop: SimpleSettingsProperty<boolean>;
    public readonly videoMuted: SimpleSettingsProperty<boolean>;
    public readonly glbAnimationOnActive: SimpleSettingsProperty<boolean>;
    public readonly glbAnimationSpeed: SimpleSettingsProperty<number>;
    public readonly glbAnimationIndex: SimpleSettingsProperty<number>;
    public readonly showLabel: SimpleSettingsProperty<boolean>;
    public readonly labelFontSize: InterpolatedProperty<number>;
    public readonly labelColor: SimpleSettingsProperty<Color>;
    public readonly labelBackgroundColor: SimpleSettingsProperty<Color>;
    public readonly labelBackgroundGradient: SimpleSettingsProperty<string>;
    public readonly labelInteractive: SimpleSettingsProperty<boolean>;
    public readonly labelFontFamily: SimpleSettingsProperty<string>;
    public readonly showConnector: SimpleSettingsProperty<boolean>;
    public readonly labelVAlign: SimpleSettingsProperty<string>;
    public readonly labelBillboardMode: SimpleSettingsProperty<boolean>;
    public readonly labelRenderMode: SimpleSettingsProperty<TextRenderMode>;
    
    // Properties for planes/rounded boxes
    public readonly cornerRadius: InterpolatedProperty<number>;
    public readonly edgeRadius: InterpolatedProperty<number>;

    /** The depth of a tile shape. */
    public readonly depth: InterpolatedProperty<number>;

    /** Manual aspect ratio override (0 = auto-detect from texture) */
    public readonly aspectRatio: InterpolatedProperty<number>;

    /** Individual opacity values for MultiPlaneShape planes */
    public readonly planeOpacity: SimpleSettingsProperty<number[]>;

    /** Individual scale values for MultiPlaneShape planes */
    public readonly planeScale: SimpleSettingsProperty<number[]>;

    // New properties for label positioning
    public readonly labelMeshPoint: InterpolatedProperty<Vector3>;
    public readonly labelDistance: InterpolatedProperty<number>;
    public readonly labelLatitude: InterpolatedProperty<number>;
    public readonly labelLongitude: InterpolatedProperty<number>;

    public readonly labelBorderRadius: InterpolatedProperty<number>;
    public readonly labelPadding: SimpleSettingsProperty<[number, number]>;
    public readonly labelBackgroundMode: SimpleSettingsProperty<'block' | 'line'>;

    /** The color of the connector line between the POI and its label. */
    public readonly connectorColor: SimpleSettingsProperty<Color>;

    /** The thickness of the connector line between the POI and its label. */
    public readonly connectorThickness: InterpolatedProperty<number>;
    public readonly labelScale: InterpolatedProperty<number>;
    public readonly labelMaxWidth: InterpolatedProperty<number>;
    /** Kameradistanz, ab der das Label voll ausgeblendet ist (0 = kein Fade). */
    public readonly labelFadeDistance: InterpolatedProperty<number>;
    /** Mobil-Vergroesserung des Labels (Default 2 = bisheriges Verhalten). */
    public readonly mobileLabelScale: InterpolatedProperty<number>;
    /**
     * Wie die Textur eine nicht-rechteckige Flaeche fuellt:
     * 'cover' = Flaeche fuellen, Bild wird an den Kanten beschnitten (Default).
     * 'contain' = Bild vollstaendig sichtbar, Restflaeche leer — bei Freistellern
     * mit transparentem Rand unsichtbar (Wunsch Alex, flora-hex #119659).
     */
    public readonly textureFit: SimpleSettingsProperty<string>;
    /** Knowledge-Objekt-ID: aktiviert das Annotations-Overlay (0 = aus). */
    public readonly knowledgeId: SimpleSettingsProperty<number>;
    /** GLB beim Laden normieren: laengste Kante = 1, Zentrum = Ursprung. */
    public readonly gltfNormalize: SimpleSettingsProperty<boolean>;

    // --- Mesh Point Indicator Properties ---
    public readonly showMeshPointNumbers: SimpleSettingsProperty<boolean>;
    public readonly meshPointColor: SimpleSettingsProperty<Color>;
    public readonly meshPointBackgroundColor: SimpleSettingsProperty<Color>;
    public readonly labelNr: InterpolatedProperty<number>;

    // --- Label Animation Properties ---
    public readonly labelOpacity: InterpolatedProperty<number>;
    public readonly fadeLabelOnActive: SimpleSettingsProperty<boolean>;
    
    // --- Label Hierarchy Properties ---
    public readonly labelParentMode: SimpleSettingsProperty<LabelParentMode>;

    // --- Static Screen Label Properties ---
    public readonly labelScreenPosition: SimpleSettingsProperty<ScreenPosition>;

    // --- New Animation Properties ---
    public readonly enableBillboard: SimpleSettingsProperty<boolean>;
    public readonly enableRotation: SimpleSettingsProperty<boolean>;
    public readonly rotateSpeed: InterpolatedProperty<number>;
    public readonly enableWiggle: SimpleSettingsProperty<boolean>;
    public readonly wiggleSpeed: InterpolatedProperty<number>;
    public readonly wiggleHeight: InterpolatedProperty<number>;
    public readonly wiggleX: SimpleSettingsProperty<boolean>;
    public readonly wiggleY: SimpleSettingsProperty<boolean>;
    public readonly wiggleZ: SimpleSettingsProperty<boolean>;

    // 🖼️ Image material settings (inheritable from scene)
    public readonly useBackgroundColor: SimpleSettingsProperty<boolean>;

    // --- POI Connection Tube Properties ---
    /** Comma-separated POI indices to connect to, e.g. "0, 2, 3" */
    public readonly connectTo: SimpleSettingsProperty<string>;
    /** Radius of connection tubes */
    public readonly tubeRadius: InterpolatedProperty<number>;
    /** Color of connection tubes */
    public readonly tubeColor: SimpleSettingsProperty<Color>;

    constructor(sceneSettings?: SceneSettingsData, appSettings?: AppSettingsData) {
        const shapeGroup = 'Shape';
        const textGroup = 'Text';
        const videoGroup = 'Video';
        const textureGroup = 'Texture';
        const labelGroup = 'Label';
        const transformGroup = 'Transform';
        const appearanceGroup = 'Appearance';
        const animationGroup = 'Animation';


        this.shape = new SimpleSettingsProperty('Shape', null, 'box', shapeGroup, [
            'none', 'box', 'sphere', 'cylinder', 'plane', 'multiplane', 'torus', 'triangle', 'hexagon', 'quader', 'tile', 'gltf', 'usdz'
        ]);

        this.markdown = new SimpleSettingsProperty('Markdown', null, '', 'Content').setMultiline(true);
        this.html = new SimpleSettingsProperty('HTML', null, '', textGroup).setMultiline(true);

        // Setup Markdown→HTML auto-parsing with external library
        this.setupMarkdownParsing();
        this.uri = new SimpleSettingsProperty('URI', null, [] as string[], textureGroup);
        // Geo defaults: absent until provided by markdown (artrack_*)
        this.artrack_latitude = new SimpleSettingsProperty('ARTrack Latitude', null, NaN, 'Geo');
        this.artrack_longitude = new SimpleSettingsProperty('ARTrack Longitude', null, NaN, 'Geo');
        this.artrack_altitude = new SimpleSettingsProperty('ARTrack Altitude', null, NaN, 'Geo');
        this.artrack_waypointId = new SimpleSettingsProperty('ARTrack Waypoint ID', null, NaN, 'Geo');
        this.textureBacksideFlipX = new SimpleSettingsProperty('Flip Backside Texture X', null, true, textureGroup);
        this.useBacksideSideMaterial = new SimpleSettingsProperty('Use Side Material for Backside', null, false, textureGroup);
        this.videoAutoplay = new SimpleSettingsProperty('Video Autoplay', null, false, videoGroup);
        this.videoLoop = new SimpleSettingsProperty('Video Loop', null, true, videoGroup);
        this.videoMuted = new SimpleSettingsProperty('Video Muted', null, true, videoGroup);
                    this.glbAnimationOnActive = new SimpleSettingsProperty('GLB Animation on Active', null, true, 'GLB');
            this.glbAnimationSpeed = new SimpleSettingsProperty('GLB Animation Speed', null, 1.0, 'GLB');
            this.glbAnimationIndex = new SimpleSettingsProperty('GLB Animation Index', null, 0, 'GLB');
        this.showLabel = new SimpleSettingsProperty('Show Label', null, true, labelGroup);
        this.showConnector = new SimpleSettingsProperty('Show Connector', null, true, labelGroup);
        this.labelVAlign = new SimpleSettingsProperty('Label Vertical Align', null, 'middle', labelGroup, ['top', 'middle', 'bottom']);
        this.labelBillboardMode = new SimpleSettingsProperty('Label Billboard Mode', null, false, labelGroup);
        this.labelRenderMode = new SimpleSettingsProperty('Label Render Mode', null, TextRenderMode.CANVAS, labelGroup, [TextRenderMode.HTML, TextRenderMode.CANVAS, TextRenderMode.SCREEN_HTML, TextRenderMode.SCREEN_HTML_STATIC]);
        
        this.labelFontFamily = new SimpleSettingsProperty('Label Font Family', null, 'Arial', labelGroup);
        
            // Plane/Box specific properties
    this.cornerRadius = new InterpolatedProperty('Corner Radius', null, 0, 0.5, shapeGroup).withBounds(0, 5, 0.01);
    this.edgeRadius = new InterpolatedProperty('Edge Radius', null, 0, 0.5, shapeGroup).withBounds(0, 1, 0.01);
            this.depth = new InterpolatedProperty('Depth', null, 0.1, 0.5, shapeGroup).withBounds(0, 10, 0.01);
        this.aspectRatio = new InterpolatedProperty('Aspect Ratio', null, 0, 0.5, shapeGroup).withBounds(0.1, 10, 0.1); // 0 = auto
        this.planeOpacity = new SimpleSettingsProperty<number[]>('Plane Opacity Array', null, [], shapeGroup); // Array of opacity values
        this.planeScale = new SimpleSettingsProperty<number[]>('Plane Scale Array', null, [], shapeGroup); // Per-plane scale multipliers for MultiPlaneShape

        this.labelMeshPoint = new InterpolatedProperty('Label Mesh Point', null, Vector3.zero(), 0.5, 'Label Position');
        
        this.labelDistance = new InterpolatedProperty('Label Distance', null, 1.0, 0.5, 'Label Position').withBounds(0, 10, 0.1);
        this.labelLatitude = new InterpolatedProperty('Label Latitude', null, 0, 0.5, 'Label Position').withBounds(-90, 90, 1);
        this.labelLongitude = new InterpolatedProperty('Label Longitude', null, 0, 0.5, 'Label Position').withBounds(-180, 180, 1);
        
        this.color = new InterpolatedProperty('Color', null, new Color('white'), 0.5, shapeGroup);
        
        this.opacity = new InterpolatedProperty('Opacity', null, 1.0, 0.5, shapeGroup).withBounds(0, 1, 0.01);
        this.scale = new InterpolatedProperty('Scale', null, 1.0, 2, shapeGroup);
        this.localTranslationOffset = new SimpleSettingsProperty('Local Offset', null, Vector3.zero(), transformGroup);

        // 🎯 CLEAN: Primary/Secondary Material Properties (roughness/metalness only)
        this.primaryRoughness = new InterpolatedProperty('Primary Roughness', null, 0.5, 0.5, 'Primary Material').withBounds(0, 1, 0.01);
        this.primaryMetalness = new InterpolatedProperty('Primary Metalness', null, 0.0, 0.5, 'Primary Material').withBounds(0, 1, 0.01);
        this.secondaryRoughness = new InterpolatedProperty('Secondary Roughness', null, 0.5, 0.5, 'Secondary Material').withBounds(0, 1, 0.01);
        this.secondaryMetalness = new InterpolatedProperty('Secondary Metalness', null, 0.0, 0.5, 'Secondary Material').withBounds(0, 1, 0.01);
        
        this.position = new SimpleSettingsProperty('Position', null, Vector3.zero(), transformGroup);
        
        this.rotation = new SimpleSettingsProperty('Rotation', null, Vector3.zero(), transformGroup);

        this.labelFontSize = new InterpolatedProperty('Label Font Size', null, 16, 0.5, labelGroup).withBounds(8, 72, 1);
        
        this.labelColor = new SimpleSettingsProperty('Label Color', null, new Color('#ffffff'), labelGroup);
        this.labelBackgroundColor = new SimpleSettingsProperty('Label Background Color', null, new Color('rgba(0,0,0,0.7)'), labelGroup);
        this.labelBackgroundGradient = new SimpleSettingsProperty('Label Background Gradient', null, '', labelGroup); // CSS gradient string
        this.labelInteractive = new SimpleSettingsProperty('Label Interactive', null, false, labelGroup); // Allow label mouse interaction
        this.labelPadding = new SimpleSettingsProperty('Label Padding', null, [4, 8] as [number, number], labelGroup);
        this.labelBorderRadius = new InterpolatedProperty('Label Border Radius', null, 5, 0.2, labelGroup).withBounds(0, 50, 1);
        this.labelBackgroundMode = new SimpleSettingsProperty('Label Background Mode', null, null, labelGroup, ['block', 'line']);
        this.connectorColor = new SimpleSettingsProperty('Connector Color', null, new Color('#ffffff'), labelGroup);
        this.connectorThickness = new InterpolatedProperty('Connector Thickness', null, 1, 0.2, labelGroup).withBounds(0, 10, 0.1);
        this.labelScale = new InterpolatedProperty('Label Scale', null, 1, 0.5, labelGroup).withBounds(0.1, 10, 0.1);
        this.labelMaxWidth = new InterpolatedProperty('Label Max Width (px)', null, 0, 0.2, labelGroup).withBounds(0, 2000, 1);
        this.labelFadeDistance = new InterpolatedProperty('Label Fade Distance', null, 0, 0.2, labelGroup).withBounds(0, 200, 0.5);
        this.mobileLabelScale = new InterpolatedProperty('Mobile Label Scale', null, 2, 0.2, labelGroup).withBounds(0.5, 4, 0.1);
        this.textureFit = new SimpleSettingsProperty<string>('Texture Fit', null, 'cover', shapeGroup, ['cover', 'contain']);
        this.knowledgeId = new SimpleSettingsProperty<number>('Knowledge Id', null, 0, shapeGroup);
        this.gltfNormalize = new SimpleSettingsProperty<boolean>('GLTF Normalize', null, false, shapeGroup);

        // --- Mesh Point Indicator Properties ---
        this.showMeshPointNumbers = new SimpleSettingsProperty('Show Mesh Point Numbers', null, false, labelGroup);
        this.meshPointColor = new SimpleSettingsProperty('Mesh Point Color', null, new Color('#ff6b6b'), labelGroup);
        this.meshPointBackgroundColor = new SimpleSettingsProperty('Mesh Point Background Color', null, new Color('#ffffff'), labelGroup);
        this.labelNr = new InterpolatedProperty('Label Number', null, 1, 0.5, labelGroup).withBounds(0, 999, 1);

        // --- Label Animation Properties ---
        this.labelOpacity = new InterpolatedProperty('Label Opacity', null, 1.0, 1, labelGroup).withBounds(0, 1, 0.01);
        this.fadeLabelOnActive = new SimpleSettingsProperty('Fade Labels On Active (Individual)', null, false, labelGroup);
        
        // --- Label Hierarchy Properties ---
        this.labelParentMode = new SimpleSettingsProperty('Label Parent Mode', null, LabelParentMode.POI, labelGroup, Object.values(LabelParentMode));

        // --- Static Screen Label Properties ---
        this.labelScreenPosition = new SimpleSettingsProperty('Label Screen Position', null, ScreenPosition.TOP_RIGHT, labelGroup, Object.values(ScreenPosition));

        // --- Initialize Animation Properties ---
        this.enableBillboard = new SimpleSettingsProperty('Shape Billboard Mode', null, false, animationGroup);
        this.enableRotation = new SimpleSettingsProperty('Enable Rotation', null, false, animationGroup);
        this.rotateSpeed = new InterpolatedProperty('Rotate Speed', null, 1, 0.5, animationGroup).withBounds(0, 100, 0.1);
        this.enableWiggle = new SimpleSettingsProperty('Enable Wiggle', null, false, animationGroup);
        this.wiggleSpeed = new InterpolatedProperty('Wiggle Speed', null, 1, 0.5, animationGroup).withBounds(0, 10, 0.1);
        this.wiggleHeight = new InterpolatedProperty('Wiggle Height', null, 0.2, 0.5, animationGroup).withBounds(0, 2, 0.05);
        this.wiggleX = new SimpleSettingsProperty('Wiggle X', null, true, animationGroup);
        this.wiggleY = new SimpleSettingsProperty('Wiggle Y', null, true, animationGroup);
        this.wiggleZ = new SimpleSettingsProperty('Wiggle Z', null, false, animationGroup);

        // 🖼️ Initialize image material settings
        this.useBackgroundColor = new SimpleSettingsProperty('Use POI Color as Background', null, false, 'Image');

        // --- Initialize POI Connection Tube Properties ---
        const connectionGroup = 'Connection';
        this.connectTo = new SimpleSettingsProperty('Connect To', null, '', connectionGroup);
        this.tubeRadius = new InterpolatedProperty('Tube Radius', null, 0.03, 0.5, connectionGroup).withBounds(0.001, 1, 0.001);
        this.tubeColor = new SimpleSettingsProperty('Tube Color', null, new Color('#ffffff'), connectionGroup);

        // --- Setup parent relationships for fallback values ---
        
        // Inherit POI-specific defaults from SceneSettings
        if (sceneSettings) {
            this.shape.addParent(sceneSettings.shape);                 // ✨ NEW: Shape inheritance from global settings
            this.color.addParent(sceneSettings.color);
            this.opacity.addParent(sceneSettings.poiOpacity);          // ✨ Fixed: Use POI-specific property from SceneSettings
            this.scale.addParent(sceneSettings.poiScale);              // ✨ Fixed: Use POI-specific property from SceneSettings
            this.labelFontSize.addParent(sceneSettings.billboardFontSize);
            this.cornerRadius.addParent(sceneSettings.cornerRadius);
            this.edgeRadius.addParent(sceneSettings.edgeRadius);
            
            // 🎯 NEW: Inherit additional POI defaults from SceneSettings
            this.depth.addParent(sceneSettings.depth);
            this.showLabel.addParent(sceneSettings.showLabel);
            this.labelScreenPosition.addParent(sceneSettings.labelScreenPosition);
            
            // 🎬 Inherit video/media defaults from SceneSettings
            this.videoAutoplay.addParent(sceneSettings.videoAutoplay);
            this.videoLoop.addParent(sceneSettings.videoLoop);
            this.videoMuted.addParent(sceneSettings.videoMuted);
            
            // 🎭 Inherit animation defaults from SceneSettings
            this.enableBillboard.addParent(sceneSettings.enableBillboard);
            this.enableRotation.addParent(sceneSettings.enableRotation);
            this.enableWiggle.addParent(sceneSettings.enableWiggle);
            this.wiggleHeight.addParent(sceneSettings.wiggleHeight);
            this.wiggleX.addParent(sceneSettings.wiggleX);
            this.wiggleY.addParent(sceneSettings.wiggleY);
            this.wiggleZ.addParent(sceneSettings.wiggleZ);
            
            // 🎯 NEW: Inherit primary/secondary material property defaults from SceneSettings
            this.primaryRoughness.addParent(sceneSettings.primaryRoughness);
            this.primaryMetalness.addParent(sceneSettings.primaryMetalness);
            this.secondaryRoughness.addParent(sceneSettings.secondaryRoughness);
            this.secondaryMetalness.addParent(sceneSettings.secondaryMetalness);
            
            // 🖼️ Inherit image material defaults from SceneSettings
            this.useBackgroundColor.addParent(sceneSettings.useBackgroundColor);
            this.useBacksideSideMaterial.addParent(sceneSettings.useBacksideSideMaterial);
        }

        // Inherit Label-specific defaults from AppSettings
        if (appSettings) {
            this.textureBacksideFlipX.addParent(appSettings.textureBacksideFlipX);

            this.labelMeshPoint.addParent(appSettings.labelMeshPoint);
            this.labelFontSize.addParent(appSettings.labelFontSize);
            this.labelColor.addParent(appSettings.labelColor);
            this.labelBackgroundColor.addParent(appSettings.labelBackgroundColor);
            this.labelBackgroundGradient.addParent(appSettings.labelBackgroundGradient);
            this.labelFontFamily.addParent(appSettings.labelFontFamily);
            this.labelVAlign.addParent(appSettings.labelVAlign);
            this.labelRenderMode.addParent(appSettings.labelRenderMode);
            // Note: labelPadding inheritance skipped due to type mismatch ([number, number] vs number | [number, number])
            this.labelBorderRadius.addParent(appSettings.labelBorderRadius);
            this.labelBackgroundMode.addParent(appSettings.labelBackgroundMode);
            this.connectorColor.addParent(appSettings.connectorColor);
            this.connectorThickness.addParent(appSettings.connectorThickness);
            this.showConnector.addParent(appSettings.showConnector);
            this.labelScale.addParent(appSettings.labelScale);
            this.labelMaxWidth.addParent(appSettings.labelMaxWidth);
            this.labelFadeDistance.addParent(appSettings.labelFadeDistance);
            this.mobileLabelScale.addParent(appSettings.mobileLabelScale);
            this.textureFit.addParent(appSettings.textureFit);
            this.labelBillboardMode.addParent(appSettings.labelBillboardMode);
            this.labelOpacity.addParent(appSettings.labelOpacity);
            this.fadeLabelOnActive.addParent(appSettings.fadeLabelOnActive);
            
            
            // 🎯 Add the missing label positioning parent relationships!
            this.labelDistance.addParent(appSettings.labelDistance);
            this.labelLatitude.addParent(appSettings.labelLatitude);
            this.labelLongitude.addParent(appSettings.labelLongitude);
            
            // 🎯 Add mesh point indicator parent relationships!
            this.showMeshPointNumbers.addParent(appSettings.showMeshPointNumbers);
            this.meshPointColor.addParent(appSettings.meshPointColor);
            this.meshPointBackgroundColor.addParent(appSettings.meshPointBackgroundColor);
            
            // 🎯 Add label hierarchy parent relationship!
            this.labelParentMode.addParent(appSettings.labelParentMode);
        }

        // 🎲 Apply randomization if enabled and no explicit values set
        this.applyRandomization(sceneSettings);
    }
    
    public getProperties(): SettingsProperty<any>[] {
        // Manually list all properties to ensure order and correctness for the UI.
        const properties: SettingsProperty<any>[] = [
            this.markdown,
            this.shape,
            this.html,
            this.uri,
            this.textureBacksideFlipX,
            this.videoAutoplay,
            this.videoLoop,
            this.videoMuted,
                            this.glbAnimationOnActive,
                this.glbAnimationSpeed,
                this.glbAnimationIndex,
            this.showLabel,
            this.showConnector,
            this.labelVAlign,
            this.labelBillboardMode,
            this.labelRenderMode,
            this.cornerRadius,
            this.edgeRadius,
            this.depth,
            this.labelMeshPoint,
            this.labelDistance,
            this.labelLatitude,
            this.labelLongitude,
            this.color,
            this.opacity,
            this.scale,
            this.localTranslationOffset,
            this.position,
            this.rotation,
            this.labelFontSize,
            this.labelColor,
            this.labelBackgroundColor,
            this.labelFontFamily,
            this.labelPadding,
            this.labelBorderRadius,
            this.labelBackgroundMode,
            this.connectorColor,
            this.connectorThickness,
            this.labelScale,
            this.labelMaxWidth,
            this.labelFadeDistance,
            this.mobileLabelScale,
            this.textureFit,
            this.knowledgeId,
            this.gltfNormalize,
            this.showMeshPointNumbers,
            this.meshPointColor,
            this.meshPointBackgroundColor,
            this.labelNr,
            this.labelOpacity,
            this.labelParentMode,
            this.labelScreenPosition,
            this.enableBillboard,
            this.enableRotation,
            this.rotateSpeed,
            this.enableWiggle,
            this.wiggleSpeed,
            this.wiggleHeight,
            this.wiggleX,
            this.wiggleY,
            this.wiggleZ,
            this.useBackgroundColor,
            this.connectTo,
            this.tubeRadius,
            this.tubeColor
        ];

        // 🎯 NEW: Add primary/secondary material properties if enabled
        // The primary/secondary material properties are now direct properties, not toggles.
        // So, they are always included.
        properties.push(
            this.primaryRoughness,
            this.primaryMetalness,
            this.secondaryRoughness,
            this.secondaryMetalness
        );
        
        return properties;
    }

    /**
     * Get all properties grouped by their group names
     * Perfect for UI generation (same pattern as AppSettingsData and SceneSettingsData)
     */
    getGroupedProperties(): { [groupName: string]: { name: string; property: any }[] } {
        const groups: { [groupName: string]: { name: string; property: any }[] } = {};
        
        // Get property list via existing method
        const properties = this.getProperties();
        
        // Find property names via reflection
        const propertyNames = Object.getOwnPropertyNames(this).filter(name => 
            this[name as keyof this] instanceof InterpolatedProperty || 
            this[name as keyof this] instanceof SimpleSettingsProperty
        );
        
        propertyNames.forEach(propertyName => {
            const property = this[propertyName as keyof this] as any;
            if (property && property.groupName) {
                const groupName = property.groupName;
                if (!groups[groupName]) {
                    groups[groupName] = [];
                }
                groups[groupName].push({ name: propertyName, property });
            }
        });
        
        return groups;
    }
    
    /**
     * Get all properties as flat list with metadata
     * (same pattern as AppSettingsData and SceneSettingsData - for consistency)
     */
    getAllProperties(): { name: string; property: any; group: string; type: string }[] {
        const grouped = this.getGroupedProperties();
        const result: { name: string; property: any; group: string; type: string }[] = [];
        
        Object.entries(grouped).forEach(([groupName, properties]) => {
            properties.forEach(({ name, property }) => {
                result.push({
                    name,
                    property,
                    group: groupName,
                    type: property.typeInfo.type
                });
            });
        });
        
        return result;
    }

    public setParent(sceneSettings: SceneSettingsData) {
        // This method can be used to re-parent properties if the scene settings change.
        this.color.addParent(sceneSettings.color);
        this.opacity.addParent(sceneSettings.poiOpacity);          // ✨ Fixed: Use POI-specific property from SceneSettings
        this.scale.addParent(sceneSettings.poiScale);              // ✨ Fixed: Use POI-specific property from SceneSettings
        this.aspectRatio.addParent(sceneSettings.aspectRatio);     // ✨ NEW: Aspect ratio inheritance
        this.labelFontSize.addParent(sceneSettings.billboardFontSize);
    }

    // Cache for property metadata to avoid recreating temp instances
    private static _cachedPropertyMetadata: Array<{ key: string, propertyName: string, type: string }> | null = null;

    /**
     * 🎲 Apply randomization based on scene settings
     */
    private applyRandomization(sceneSettings?: SceneSettingsData): void {
        if (!sceneSettings) return;

        // Apply random texture if enabled and no own value was explicitly set
        // 🚫 IMPORTANT: Skip random texture for shapes that don't use external textures
        const shapeValue = this.shape.value?.toLowerCase();
        const isTextureCompatibleShape = !['gltf', 'usdz'].includes(shapeValue || '');
        
        if (sceneSettings.randomTexture.value && !this.uri.isOwnValue && isTextureCompatibleShape) {
            const randomId = Math.floor(Math.random() * 1000) + 1;
            const width = 512;
            const height = 512;
            const randomuri = `https://picsum.photos/${width}/${height}?random=${randomId}`;
            
            this.uri.value = [randomuri]; // Now an array
            //console.log(`🎲 Applied random texture: ${randomuri} (shape: ${shapeValue})`);
        } else if (sceneSettings.randomTexture.value && !isTextureCompatibleShape) {
            //console.log(`🚫 Skipped random texture for shape '${shapeValue}' (uses own materials)`);
        }

        // Apply random color if enabled and no own value was explicitly set
        if (sceneSettings.randomColor.value && !this.color.isOwnValue) {
            // Generate random HSL color for better variety
            const hue = Math.random() * 360;
            const saturation = 60 + Math.random() * 40; // 60-100% saturation for vibrant colors
            const lightness = 40 + Math.random() * 30;  // 40-70% lightness for good visibility
            
            const randomColor = Color.fromHsl(hue, saturation, lightness);
            this.color.value = randomColor;
            //console.log(`🎲 Applied random color: ${randomColor.toString()}`);
        }
    }

    /**
     * Setup automatic Markdown→HTML parsing
     */
    private setupMarkdownParsing(): void {
        // Parse initial markdown to HTML
        this.parseMarkdownToHtml().catch(console.error);
        
        // Auto-parse whenever markdown changes
        this.markdown.on(() => {
            this.parseMarkdownToHtml().catch(console.error);
        });
    }

    /**
     * Convert markdown to HTML using the 'marked' library
     */
    private async parseMarkdownToHtml(): Promise<void> {
        const markdownText = this.markdown.value || '';
        if (!markdownText.trim()) {
            this.html.value = '';
            return;
        }

        try {
            // Configure marked for safe HTML output
            marked.setOptions({
                breaks: true,        // Convert \n to <br>
                gfm: true           // GitHub Flavored Markdown
            });

            const htmlText = await marked.parse(markdownText);
            this.html.value = htmlText;
        } catch (error) {
            console.error('Error parsing markdown:', error);
            this.html.value = markdownText; // Fallback to raw text
        }
    }

    /**
     * 🎯 Auto-generated property metadata via reflection
     * Uses existing property system - ZERO maintenance needed!
     */
    public static getPropertyMetadata(): Array<{ key: string, propertyName: string, type: string }> {
        // Return cached result if available
        if (POIData._cachedPropertyMetadata) {
            return POIData._cachedPropertyMetadata;
        }

        // Create a temporary instance to get property metadata via reflection
        const tempInstance = new POIData(undefined);
        const properties = tempInstance.getProperties();
        const instanceKeys = Object.keys(tempInstance);
        const metadata: Array<{ key: string, propertyName: string, type: string }> = [];

        for (const prop of properties) {
            // Find the property name in the instance
            const propertyName = instanceKeys.find(key => (tempInstance as any)[key] === prop);
            
            if (propertyName) {
                // Convert property name to markdown-friendly key (camelCase -> lowercase)
                const markdownKey = propertyName.toLowerCase();
                
                // Get type from the property's typeInfo
                let type = prop.typeInfo?.type || 'string';
                
                // Map complex types to simple markdown types
                if (type === 'object') {
                    if (prop.typeInfo?.subtype === 'Vector3') {
                        type = 'vector3';
                    } else if (prop.typeInfo?.subtype === 'Color') {
                        type = 'color';
                    } else {
                        type = 'object';
                    }
                }
                
                metadata.push({
                    key: markdownKey,
                    propertyName: propertyName,
                    type: type
                });
            }
        }

        // Cache the result
        POIData._cachedPropertyMetadata = metadata;
        return metadata;
    }
}
